import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import { FontVariantToken } from '@uipath/apollo-core';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import { toISODate } from '../../../util/MigrationUtil';

/** Generate localized day abbreviations starting from Monday */
const getLocalizedDayLabels = (locale: string): { short: string[]; narrow: string[] } => {
    const short: string[] = [];
    const narrow: string[] = [];
    // Jan 5, 2026 is a Monday
    for (let i = 0; i < 7; i++) {
        const date = new Date(2026, 0, 5 + i);
        short.push(date.toLocaleDateString(locale, { weekday: 'short' }));
        narrow.push(date.toLocaleDateString(locale, { weekday: 'narrow' }));
    }
    return {
        short,
        narrow,
    };
};

const useStyles = makeStyles(theme => ({
    calendarContainer: {
        display: 'flex',
        gap: '32px',
    },
    monthContainer: { flex: 1 },
    monthHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '8px 0 12px',
    },
    calendarGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '4px',
        textAlign: 'center',
    },
    calendarHeaderCell: {
        fontWeight: 600,
        fontSize: '12px',
        padding: '8px 0',
        color: theme.palette.semantic.colorForegroundDeEmp,
    },
    calendarDay: {
        padding: '8px',
        borderRadius: '4px',
        fontSize: '13px',
        cursor: 'pointer',
        '&:hover': { backgroundColor: theme.palette.semantic.colorBackgroundHover },
    },
    calendarDaySelected: {
        padding: '8px',
        borderRadius: '4px',
        fontSize: '13px',
        cursor: 'pointer',
        backgroundColor: theme.palette.semantic.colorPrimary,
        color: theme.palette.semantic.colorForegroundInverse,
        fontWeight: 600,
    },
    calendarDayDisabled: {
        padding: '8px',
        borderRadius: '4px',
        fontSize: '13px',
        color: theme.palette.semantic.colorForegroundDisable,
    },
    dayPillContainer: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    dayPill: { cursor: 'pointer' },
    dayPillNone: {
        cursor: 'pointer',
        backgroundColor: theme.palette.semantic.colorBackground,
        border: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
        color: theme.palette.semantic.colorForeground,
        '&:hover': { backgroundColor: theme.palette.semantic.colorBackgroundHover },
    },
    dayPillPartial: {
        cursor: 'pointer',
        backgroundColor: theme.palette.semantic.colorPrimary,
        border: `1px solid ${theme.palette.semantic.colorPrimary}`,
        color: theme.palette.semantic.colorForegroundInverse,
        fontWeight: 600,
        opacity: 0.6,
        '&:hover': {
            backgroundColor: theme.palette.semantic.colorPrimary,
            opacity: 0.75,
        },
    },
    dayPillAll: {
        cursor: 'pointer',
        backgroundColor: theme.palette.semantic.colorPrimary,
        border: `1px solid ${theme.palette.semantic.colorPrimary}`,
        color: theme.palette.semantic.colorForegroundInverse,
        fontWeight: 600,
        '&:hover': {
            backgroundColor: theme.palette.semantic.colorPrimary,
            opacity: 0.85,
        },
    },
    calendarBox: {
        border: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
        borderRadius: '8px',
        padding: '16px',
    },
    calendarNavRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 0 12px',
    },
}));

export interface MigrationScheduleCalendarProps {
    selectedDates: string[];
    onDatesChange: (dates: string[]) => void;
    minDate: Date;
    maxDate: Date;
    maxSlots?: number;
}

/** Get the ISO day of week: 0=Mon, 1=Tue, ... 6=Sun */
const getISODayOfWeek = (d: Date): number => (d.getDay() + 6) % 7;

/** Generates calendar grid weeks for a given year/month */
const getMonthWeeks = (year: number, month: number): Array<Array<Date | null>> => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = getISODayOfWeek(firstDay);

    const weeks: Array<Array<Date | null>> = [];
    let currentWeek: Array<Date | null> = [];

    for (let i = 0; i < startDow; i++) {
        currentWeek.push(null);
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
        currentWeek.push(new Date(year, month, d));
        if (currentWeek.length === 7) {
            weeks.push(currentWeek);
            currentWeek = [];
        }
    }

    if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
            currentWeek.push(null);
        }
        weeks.push(currentWeek);
    }

    return weeks;
};

/** Gets all dates matching a specific day-of-week within [minDate, maxDate] */
const getDatesForDayOfWeek = (dow: number, minDate: Date, maxDate: Date): string[] => {
    const dates: string[] = [];
    const current = new Date(minDate);
    current.setHours(0, 0, 0, 0);

    while (current <= maxDate) {
        if (getISODayOfWeek(current) === dow) {
            dates.push(toISODate(current));
        }
        current.setDate(current.getDate() + 1);
    }

    return dates;
};

const CalendarMonth: React.FC<{
    year: number;
    month: number;
    selectedSet: Set<string>;
    minDate: Date;
    maxDate: Date;
    onToggleDate: (dateStr: string) => void;
    maxReached: boolean;
    classes: ReturnType<typeof useStyles>;
    locale: string;
    translate: (descriptor: { id: string }, values?: Record<string, string>) => string;
    dayLabels: { short: string[]; narrow: string[] };
    focusableDate?: string;
}> = ({
    year, month, selectedSet, minDate, maxDate, onToggleDate, maxReached, classes, locale, translate, dayLabels, focusableDate,
}) => {
    const weeks = useMemo(() => getMonthWeeks(year, month), [ year, month ]);

    return (
        <div className={classes.monthContainer}>
            <div
                className={classes.calendarGrid}
                role="grid"
                aria-label={new Date(year, month).toLocaleDateString(locale, {
                    month: 'long',
                    year: 'numeric',
                })}>
                {dayLabels.narrow.map((d, i) => (
                    <div
                        key={dayLabels.short[i]}
                        role="columnheader"
                        className={classes.calendarHeaderCell}>
                        {d}
                    </div>
                ))}
                {weeks.flat().map((date, i) => {
                    if (!date) {
                        return <div key={`empty-${i}`} />;
                    }

                    const dateStr = toISODate(date);
                    const isSelected = selectedSet.has(dateStr);
                    const isDisabled = date < minDate || date > maxDate;
                    const isMaxedOut = maxReached && !isSelected;

                    if (isDisabled || isMaxedOut) {
                        return (
                            <div
                                key={dateStr}
                                role="gridcell"
                                aria-disabled="true"
                                className={classes.calendarDayDisabled}>
                                {date.getDate()}
                            </div>
                        );
                    }

                    return (
                        <div
                            key={dateStr}
                            role="gridcell"
                            tabIndex={dateStr === focusableDate ? 0 : -1}
                            aria-selected={isSelected}
                            aria-label={translate(
                                { id: isSelected ? 'CLIENT_MIGRATION_CALENDAR_DESELECT_DATE' : 'CLIENT_MIGRATION_CALENDAR_SELECT_DATE' },
                                {
                                    date: date.toLocaleDateString(locale, {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric',
                                    }),
                                },
                            )}
                            className={isSelected ? classes.calendarDaySelected : classes.calendarDay}
                            onClick={() => onToggleDate(dateStr)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onToggleDate(dateStr);
                                }
                            }}>
                            {date.getDate()}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const MigrationScheduleCalendar: React.FC<MigrationScheduleCalendarProps> = ({
    selectedDates,
    onDatesChange,
    minDate,
    maxDate,
    maxSlots = 60,
}) => {
    const classes = useStyles();
    const {
        formatMessage: translate, locale,
    } = useIntl();
    const dayLabels = useMemo(() => getLocalizedDayLabels(locale), [ locale ]);

    const [ viewStartMonth, setViewStartMonth ] = useState<{ year: number; month: number }>(() => ({
        year: minDate.getFullYear(),
        month: minDate.getMonth(),
    }));

    const selectedSet = useMemo(() => new Set(selectedDates), [ selectedDates ]);

    // Auto-scroll to the first selected date when dates are pre-populated
    const hasAutoScrolled = useRef(false);
    useEffect(() => {
        if (!hasAutoScrolled.current && selectedDates.length > 0) {
            hasAutoScrolled.current = true;
            const [ year, month ] = selectedDates[0].split('-').map(Number);
            setViewStartMonth({
                year,
                month: month - 1,
            });
        }
    }, [ selectedDates ]);

    const maxReached = selectedDates.length >= maxSlots;

    /** Compute the single focusable date for roving tabindex — first selected, or first selectable */
    const focusableDate = useMemo(() => {
        if (selectedDates.length > 0) {
            return selectedDates[0];
        }
        const d = new Date(Math.max(minDate.getTime(), Date.now()));
        d.setHours(0, 0, 0, 0);
        return toISODate(d <= maxDate ? d : minDate);
    }, [ selectedDates, minDate, maxDate ]);

    const secondMonth = useMemo(() => {
        const d = new Date(viewStartMonth.year, viewStartMonth.month + 1);
        return {
            year: d.getFullYear(),
            month: d.getMonth(),
        };
    }, [ viewStartMonth ]);

    const canGoBack = useMemo(() => {
        const minMonth = new Date(minDate.getFullYear(), minDate.getMonth());
        const current = new Date(viewStartMonth.year, viewStartMonth.month);
        return current > minMonth;
    }, [ viewStartMonth, minDate ]);

    const canGoForward = useMemo(() => {
        const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth());
        const secondMonthDate = new Date(secondMonth.year, secondMonth.month);
        return secondMonthDate < maxMonth;
    }, [ secondMonth, maxDate ]);

    const handlePrev = useCallback(() => {
        setViewStartMonth(prev => {
            const d = new Date(prev.year, prev.month - 1);
            return {
                year: d.getFullYear(),
                month: d.getMonth(),
            };
        });
    }, []);

    const handleNext = useCallback(() => {
        setViewStartMonth(prev => {
            const d = new Date(prev.year, prev.month + 1);
            return {
                year: d.getFullYear(),
                month: d.getMonth(),
            };
        });
    }, []);

    const handleToggleDate = useCallback((dateStr: string) => {
        if (selectedSet.has(dateStr)) {
            onDatesChange(selectedDates.filter(d => d !== dateStr));
        } else if (selectedDates.length < maxSlots) {
            onDatesChange([ ...selectedDates, dateStr ].sort((a, b) => a.localeCompare(b)));
        }
    }, [ selectedDates, selectedSet, maxSlots, onDatesChange ]);

    /** Map of day-of-week -> all matching dates in range (expensive, only depends on date bounds) */
    const dowDatesMap = useMemo(() => {
        const map = new Map<number, string[]>();
        for (let dow = 0; dow < 7; dow++) {
            map.set(dow, getDatesForDayOfWeek(dow, minDate, maxDate));
        }
        return map;
    }, [ minDate, maxDate ]);

    /** Derive day-of-week pill states: 'none' | 'partial' | 'all' */
    const pillStates = useMemo(() => {
        const result = new Map<number, 'none' | 'partial' | 'all'>();
        for (let dow = 0; dow < 7; dow++) {
            const allDates = dowDatesMap.get(dow)!;
            const selectedCount = allDates.filter(d => selectedSet.has(d)).length;
            if (selectedCount === 0) {
                result.set(dow, 'none');
            } else if (selectedCount === allDates.length) {
                result.set(dow, 'all');
            } else {
                result.set(dow, 'partial');
            }
        }
        return result;
    }, [ dowDatesMap, selectedSet ]);

    const handleToggleDayPill = useCallback((dow: number) => {
        const dowDates = dowDatesMap.get(dow)!;
        const state = pillStates.get(dow);

        if (state === 'all') {
            const removeSet = new Set(dowDates);
            onDatesChange(selectedDates.filter(d => !removeSet.has(d)));
        } else {
            const merged = new Set([ ...selectedDates, ...dowDates ]);
            const sorted = Array.from(merged).sort((a, b) => a.localeCompare(b));
            onDatesChange(sorted.slice(0, maxSlots));
        }
    }, [ pillStates, selectedDates, dowDatesMap, maxSlots, onDatesChange ]);

    return (
        <UiStack
            direction="column"
            gap={SpacingToken.M}>
            <UiStack
                direction="column"
                gap={SpacingToken.XS}>
                <UiText variant={FontVariantToken.fontSizeMBold}>
                    {translate({ id: 'CLIENT_MIGRATION_QUICK_SELECT_BY_DAY' })}
                </UiText>
                <div className={classes.dayPillContainer}>
                    {dayLabels.short.map((day, idx) => {
                        const state = pillStates.get(idx) ?? 'none';
                        const pillClass = state === 'all'
                            ? classes.dayPillAll
                            : state === 'partial'
                                ? classes.dayPillPartial
                                : classes.dayPillNone;
                        return (
                            <Chip
                                key={day}
                                label={day}
                                onClick={() => handleToggleDayPill(idx)}
                                className={pillClass}
                            />
                        );
                    })}
                </div>
                <UiText variant={FontVariantToken.fontSizeS}>
                    {translate({ id: 'CLIENT_MIGRATION_DAY_PILL_HINT' })}
                </UiText>
            </UiStack>

            <UiStack
                direction="column"
                gap={SpacingToken.XS}>
                <UiText variant={FontVariantToken.fontSizeMBold}>
                    {translate({ id: 'CLIENT_MIGRATION_PICK_INDIVIDUAL_DATES' })}
                </UiText>
                <div className={classes.calendarBox}>
                    <div className={classes.calendarNavRow}>
                        <IconButton
                            onClick={handlePrev}
                            disabled={!canGoBack}
                            size="small"
                            aria-label={translate({ id: 'CLIENT_MIGRATION_CALENDAR_PREV' })}>
                            <ChevronLeftIcon />
                        </IconButton>
                        <UiText variant={FontVariantToken.fontSizeMBold}>
                            {new Date(viewStartMonth.year, viewStartMonth.month).toLocaleDateString(locale, {
                                month: 'long',
                                year: 'numeric',
                            })}
                        </UiText>
                        <div style={{ flex: 1 }} />
                        <UiText variant={FontVariantToken.fontSizeMBold}>
                            {new Date(secondMonth.year, secondMonth.month).toLocaleDateString(locale, {
                                month: 'long',
                                year: 'numeric',
                            })}
                        </UiText>
                        <IconButton
                            onClick={handleNext}
                            disabled={!canGoForward}
                            size="small"
                            aria-label={translate({ id: 'CLIENT_MIGRATION_CALENDAR_NEXT' })}>
                            <ChevronRightIcon />
                        </IconButton>
                    </div>
                    <div className={classes.calendarContainer}>
                        <CalendarMonth
                            year={viewStartMonth.year}
                            month={viewStartMonth.month}
                            selectedSet={selectedSet}
                            minDate={minDate}
                            maxDate={maxDate}
                            onToggleDate={handleToggleDate}
                            maxReached={maxReached}
                            classes={classes}
                            locale={locale}
                            translate={translate}
                            dayLabels={dayLabels}
                            focusableDate={focusableDate}
                        />
                        <CalendarMonth
                            year={secondMonth.year}
                            month={secondMonth.month}
                            selectedSet={selectedSet}
                            minDate={minDate}
                            maxDate={maxDate}
                            onToggleDate={handleToggleDate}
                            maxReached={maxReached}
                            classes={classes}
                            locale={locale}
                            translate={translate}
                            dayLabels={dayLabels}
                            focusableDate={focusableDate}
                        />
                    </div>
                </div>
            </UiStack>

            <UiText variant={FontVariantToken.fontSizeS}>
                {translate({ id: 'CLIENT_MIGRATION_SLOT_COUNTER' }, {
                    count: selectedDates.length,
                    max: maxSlots,
                })}
            </UiText>
        </UiStack>
    );
};
